import { useState } from 'react'
import { useSelector } from 'react-redux'
import { LabeledText } from 'src/components/UI'
import Modal from 'src/components/UI/CustomUI/molecules/Modal'
import { getEstimate } from 'src/ducks/estimate/selectors'
import { EstimateItem } from 'src/ducks/types'
import { formatTimestamp } from 'src/helpers'
import { useIsLgScreen, useIsXlScreen } from 'src/hooks'
import { ContentBox, Divider, TabBox } from '../../Templates'
import SitevisitsModal from './SiteVisitsModal'

// Preferred time windows are stored as UTC timestamps with specific hours (8 or 12)
// to encode morning vs afternoon slots. We display in property timezone if available,
// otherwise browser timezone, with America/New_York as final fallback.
const PREFERRED_TIME_WINDOW_UTC_HOUR: Record<'morning' | 'afternoon', number> = {
  morning: 8,
  afternoon: 12,
}

const getPreferredTimeWindowLabel = (timestamp?: number | null, propertyTimezone?: string): string => {
  if (!timestamp) return '-'
  
  // Determine timezone: property > browser > fallback
  let timezone: string
  try {
    timezone = propertyTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York'
  } catch {
    timezone = 'America/New_York'
  }
  
  const date = new Date(timestamp * 1000)
  const hour = parseInt(date.toLocaleString('en-US', { hour: 'numeric', hour12: false, timeZone: timezone }))
  
  if (hour === PREFERRED_TIME_WINDOW_UTC_HOUR.morning) return 'Morning (8am - 12pm)'
  if (hour === PREFERRED_TIME_WINDOW_UTC_HOUR.afternoon) return 'Afternoon (12pm - 5pm)'
  return '-'
}

const EstimateInfo = () => {
  const estimate = useSelector(getEstimate())
  const [xlScreen] = useIsXlScreen()
  const [lgScreen] = useIsLgScreen()
  const [modalOpen, setModalOpen] = useState(false)

  const repairTimeLineMapper = {
    PRELISTING: 'Prelisting',
    ASAP: 'ASAP',
    BEFORE_CLOSE: 'Before Close',
    AFTER_CLOSE: 'After Close',
    NEVER: 'Never',
    ONE_WEEK: 'One Week',
    THIRTY_DAYS: 'Thirty Days',
    SIXTY_DAYS: 'Sixty Days',
    OVER_SIXTY_DAYS: 'Over Sixty Days'
  }

  const {
    properties,
    createdOn,
    deliveryService,
    repairTimeline,
  } = estimate || {} as EstimateItem

  const { address, territory, estimateDeliveredOn, preferredStartDate } = properties || {}

  const estimateSent = estimateDeliveredOn ? formatTimestamp(estimateDeliveredOn, 'MM/DD/YYYY') : '-'
  const deliveryRequested = deliveryService?.title || '-'
  const deliveryDays = deliveryService?.days || '-'
  const repairTimelineValue = repairTimeLineMapper[repairTimeline] || '-'
  const preferredStartDateValue = preferredStartDate ? formatTimestamp(preferredStartDate, 'MM/DD/YYYY') : '-'
  const preferredTimeWindowValue = getPreferredTimeWindowLabel(preferredStartDate, address?.timeZone)

  return (
    <TabBox>
      <Modal
        setOpen={setModalOpen}
        open={modalOpen}
        title='Site Visit History'
        showClose
        size='xs'
      >
        <SitevisitsModal />
      </Modal>
      <ContentBox gridTemplateColumns={'1fr'}>
        <LabeledText labelValue='Address:' textValue={estimate?.properties.address?.line1} />
        <ContentBox gridTemplateColumns={xlScreen || lgScreen ? '1fr 1fr' : '1fr'}>
          <LabeledText labelValue='City:' textValue={address?.city} />
          <LabeledText labelValue='State:' textValue={address?.state} />
          <LabeledText labelValue='Territory:' textValue={territory?.title} />
          <LabeledText labelValue='ZIP Code:' textValue={address?.zipCode} />
        </ContentBox>
      </ContentBox>
      <Divider />
      <ContentBox gridTemplateColumns={xlScreen || lgScreen ? '1fr 1fr' : '1fr'}>
        <LabeledText
          labelValue='Created On:'
          textValue={formatTimestamp(createdOn, 'MM/DD/YYYY')}
          flexDirection={'column'}
        />
        <LabeledText
          labelValue='Estimate Sent:'
          textValue={estimateSent}
          flexDirection={'column'}
        />
      </ContentBox>
      <ContentBox gridTemplateColumns={xlScreen || lgScreen ? '1fr 1fr 1fr' : '1fr'}>
        <LabeledText
          labelValue='Delivery Requested:'
          textValue={deliveryRequested}
          flexDirection={'column'}
        />
        <LabeledText
          labelValue='Repairs Requested:'
          textValue={repairTimelineValue}
          flexDirection={'column'}
        />
        <LabeledText
          labelValue='Delivery Days:'
          textValue={`${deliveryDays}`}
          flexDirection={'column'}
        />
        <LabeledText
          labelValue='Preferred Start Date:'
          textValue={preferredStartDateValue}
          flexDirection={'column'}
        />
        <LabeledText
          labelValue='Preferred Time:'
          textValue={preferredTimeWindowValue}
          flexDirection={'column'}
        />
      </ContentBox>
    </TabBox>
  )
}

export default EstimateInfo
